import { FC, useContext, useEffect, useMemo } from "react";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { ProviderHubFilters, ContractEntityProvidersFilters } from "@app/projects/network-assessment/provider-hub/interfaces";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { getSelectionText } from "@app/shared/utility/getSelectionText";
import { formatSpecialtyWithCode } from "@app/projects/network-assessment/provider-hub/utility";

interface ContractEntityFiltersProps {
    activeServiceAreaStatusFilters: Set<RegionSetStatusType>;
    handleResetAll: () => void;
    handleSearchClick: () => void;
    serviceAreaMatchFilter: (child: DropdownItem<string>, activeKeys: string[]) => boolean;
    serviceAreaStatusFilters: { key: string; displayValue: string }[];
    setActiveServiceAreaStatusFilters: React.Dispatch<React.SetStateAction<Set<RegionSetStatusType>>>;
    setProviderHubFilter: <T extends keyof ProviderHubFilters>(key: T, value: ProviderHubFilters[T]) => void;
    filterProviderHubState: ProviderHubFilters;
    filterState: ContractEntityProvidersFilters;
    setFilter: <T extends keyof ContractEntityProvidersFilters>(key: T, value: ContractEntityProvidersFilters[T]) => void;
    specialtyOptions: DropdownItem<string>[];
    setSpecialtyOptions: React.Dispatch<React.SetStateAction<DropdownItem<string>[]>>;
    serviceAreaOptions: NestingParentDropdownItem[];
    setServiceAreaOptions: React.Dispatch<React.SetStateAction<NestingParentDropdownItem[]>>;
    selectedProviderNetworkTypes: ProviderNetworkTypeEnum[];
    setSelectedProviderNetworkTypes: React.Dispatch<React.SetStateAction<ProviderNetworkTypeEnum[]>>;
    providerNetworkTypeOptions: NestingParentDropdownItem[];
    // the profile slots its inline "View by" dropdown in here so it renders alongside the filters.
    children?: React.ReactNode;
}

export const ContractEntityFilters: FC<React.PropsWithChildren<ContractEntityFiltersProps>> = (props: ContractEntityFiltersProps) => {
    const projectContext = useContext(CountyProjectContext);
    const allOptionsText = "All";

    useEffect(() => {
        const sortedSpecialtyGroups = projectContext.runConfiguration.specialtyGroups.sort((a, b) => a.name.localeCompare(b.name));
        const specialties = sortedSpecialtyGroups.map(item => ({
            key: item.code,
            name: formatSpecialtyWithCode(item),
        }));
        props.setSpecialtyOptions(specialties);
    }, []);

    const providerNetworkTypeSelectionText = useMemo(() => {
        if (props.filterState.providerNetworkTypes.length === 0) {
            return allOptionsText;
        }
        const totalProviderNetworkTypeOptions = props.providerNetworkTypeOptions.flatMap(parent =>
            parent.children.length > 0 ? parent.children.map(child => child.name) : [parent.name]
        );
        return getSelectionText(props.filterState.providerNetworkTypes, totalProviderNetworkTypeOptions)
    }, [props.providerNetworkTypeOptions, props.filterState.providerNetworkTypes])

    const specialtySelectionText = useMemo(() => {
        if (props.filterState.specialtyGroupCodes.length === 0) {
            return allOptionsText;
        }
        return getSelectionText(props.filterState.specialtyGroupCodes, props.specialtyOptions);
    }, [props.filterState.specialtyGroupCodes, props.specialtyOptions]);

    const serviceAreaSelectionText = useMemo(() => {
        if (props.filterState.regionSetIds.length === 0) {
            return allOptionsText;
        }
        return getSelectionText(props.filterState.regionSetIds, props.serviceAreaOptions?.flatMap(sa => sa.children));
    }, [props.filterState.regionSetIds, props.serviceAreaOptions]);

    return (
        <>
            <div className="provider-hub__landing-page-top-actions-container">

                {/* the profile slots the Facilities/Practitioners "View by" dropdown here so it sits inline with the filters. */}
                {props.children}

                <MultiselectNestedDropdown
                    items={props.providerNetworkTypeOptions}
                    customSelectInputText={providerNetworkTypeSelectionText}
                    containerClassName="item-column"
                    label="Provider Source"
                    hideParentCheckboxes={false}
                    parentsAsValues
                    keepParentOpen
                    onSelectionChanged={(selectedItems: string[]) => {
                        props.setSelectedProviderNetworkTypes(
                            selectedItems.map(item => item as ProviderNetworkTypeEnum)
                        );
                        props.setFilter('providerNetworkTypes', selectedItems.map(item => item as ProviderNetworkTypeEnum));
                        props.setProviderHubFilter('providerNetworkTypes', selectedItems.map(item => item as ProviderNetworkTypeEnum));
                    }}
                    selectedItems={props.selectedProviderNetworkTypes}
                    testId="provider-hub__filter-provider-source-multi-select"
                />

                <MultiselectNestedDropdown
                    containerClassName="item-column"
                    customSelectInputText={serviceAreaSelectionText}
                    filters={props.serviceAreaStatusFilters}
                    items={props.serviceAreaOptions}
                    label="Service Area"
                    matchFilter={props.serviceAreaMatchFilter}
                    onSelectedFiltersChange={(keys) =>
                        props.setActiveServiceAreaStatusFilters(new Set(keys as RegionSetStatusType[]))
                    }
                    onSelectionChanged={(selectedItems) => {
                        props.setFilter('regionSetIds', selectedItems.map(Number));
                        props.setProviderHubFilter('regionSetIds', selectedItems.map(Number));
                    }}
                    selectedFilters={[...props.activeServiceAreaStatusFilters]}
                    selectedItems={props.filterState.regionSetIds.map(String)}
                    testId="provider-hub__service-areas-multi-select-dropdown"
                />

                <MultiselectDropdown
                    containerClassName="item-column"
                    customWidth={true}
                    customSelectInputText={specialtySelectionText}
                    items={props.specialtyOptions}
                    label="Specialty Group"
                    onSelectionChanged={(selectedItems) => {
                        props.setFilter('specialtyGroupCodes', selectedItems);
                        props.setProviderHubFilter('specialtyGroupCodes', selectedItems);
                    }}
                    selectedItems={props.filterState.specialtyGroupCodes}
                    testId="provider-hub__specialties-multi-select-dropdown"
                    showTooltipOnOverflow={true}
                />

                <QESButton
                    className="item-column action-buttons"
                    qesType="secondary"
                    qesTestId="provider-hub__reset-all-button"
                    onClick={props.handleResetAll}>
                    Reset All
                </QESButton>

                <QESButton
                    className="item-column action-buttons"
                    id="provider-hub__search-button"
                    qesType="primary"
                    qesTestId="provider-hub__search-button"
                    onClick={props.handleSearchClick}>
                    Search
                </QESButton>
            </div>
        </>
    )
}